import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { format } from 'date-fns';

interface SerialDetail {
  Id: number;
  SerialNumber: string;
  Status: string;
  Rate: number;
  MfgDate: string;
  ExpiryDays: number;
  BatchNo: string;
  LocationID: number;
  CostPrice: number;
}

interface SerialSelectionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onAddToList: (selectedSerials: SerialDetail[]) => void;
  serialDetails: SerialDetail[];
  productName: string;
  barcode: string;
  excludedSerialNumbers?: Set<string>; // Serial numbers already added to prevent duplicates
}

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 50];

const SerialSelectionPopup: React.FC<SerialSelectionPopupProps> = ({
  isOpen,
  onClose,
  onAddToList,
  serialDetails,
  productName,
  barcode,
  excludedSerialNumbers = new Set(),
}) => {
  const [selectedSerials, setSelectedSerials] = useState<Set<number>>(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Detect mobile viewport
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handle = () => setIsMobile(mq.matches);
    handle();
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  // Reset selection and page when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedSerials(new Set());
      setCurrentPage(1);
    }
  }, [isOpen]);

  // Filter out excluded serial numbers (from product JSON SerialDetails)
  const availableSerials = serialDetails.filter(
    (serial) => !excludedSerialNumbers.has(serial.SerialNumber)
  );
  const totalPages = Math.max(1, Math.ceil(availableSerials.length / pageSize));
  useEffect(() => {
    setCurrentPage((p) => (p > totalPages ? 1 : p));
  }, [totalPages, availableSerials.length]);

  // Paginated slice for desktop table only (Sales Invoice serial popup)
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedSerials = availableSerials.slice(startIdx, startIdx + pageSize);

  // Mobile always shows ALL cards; per-page dropdown only affects desktop table
  const serialsForMobile = availableSerials;

  const handlePageSizeChange = (value: string) => {
    const size = Number(value);
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleCheckboxChange = (serialId: number, checked: boolean) => {
    setSelectedSerials((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(serialId);
      } else {
        newSet.delete(serialId);
      }
      return newSet;
    });
  };

  const handleSelectAllChange = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(availableSerials.map((serial) => serial.Id));
      setSelectedSerials(allIds);
    } else {
      setSelectedSerials(new Set());
    }
  };

  const handleAddToList = () => {
    const selected = availableSerials.filter((serial) =>
      selectedSerials.has(serial.Id)
    );
    if (selected.length > 0) {
      onAddToList(selected);
      setSelectedSerials(new Set());
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd-MM-yyyy');
    } catch {
      return dateString;
    }
  };

  const allSelected =
    availableSerials.length > 0 &&
    availableSerials.every((serial) => selectedSerials.has(serial.Id));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0 w-[95vw] sm:w-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50 shrink-0">
          <DialogTitle className="text-lg sm:text-xl font-semibold">
            Select Serial Numbers - {productName} ({barcode})
          </DialogTitle>
        </div>

        {/* Table Section - scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto px-2 sm:px-4 py-4">
          {availableSerials.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {excludedSerialNumbers.size > 0
                ? 'All serial numbers have been added'
                : 'No serial numbers available'}
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden sm:block border rounded-lg overflow-hidden">
                {/* Pagination: above table on desktop */}
                {!isMobile && availableSerials.length > 0 && (
                  <div className="flex items-center justify-between px-2 py-3 border-b bg-gray-50/80 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">
                        Showing {startIdx + 1}–{Math.min(startIdx + pageSize, availableSerials.length)} of {availableSerials.length} serial numbers
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-gray-600 whitespace-nowrap">Per page:</span>
                        <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                          <SelectTrigger className="h-8 w-[70px] text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAGE_SIZE_OPTIONS.map((n) => (
                              <SelectItem key={n} value={String(n)}>
                                {n}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-gray-700 min-w-[100px] text-center">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-gray-100 z-10">
                      <TableRow>
                        <TableHead className="min-w-[50px]">
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={handleSelectAllChange}
                            className="scale-75"
                          />
                        </TableHead>
                        <TableHead className="min-w-[150px]">Serial Number</TableHead>
                        <TableHead className="min-w-[100px]">Rate</TableHead>
                        <TableHead className="min-w-[120px]">Mfg Date</TableHead>
                        <TableHead className="min-w-[120px]">Expiry Days</TableHead>
                        <TableHead className="min-w-[120px]">Batch No</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedSerials.map((serial, index) => (
                        <TableRow
                          key={serial.Id}
                          className={`hover:bg-gray-50 ${
                            index % 2 === 1 ? 'bg-muted/30' : ''
                          }`}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedSerials.has(serial.Id)}
                              onCheckedChange={(checked) =>
                                handleCheckboxChange(serial.Id, !!checked)
                              }
                              className="scale-75"
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {serial.SerialNumber}
                          </TableCell>
                          <TableCell>₹{serial.Rate.toFixed(2)}</TableCell>
                          <TableCell>{formatDate(serial.MfgDate)}</TableCell>
                          <TableCell>
                            {serial.ExpiryDays === 999999
                              ? 'Forever'
                              : serial.ExpiryDays}
                          </TableCell>
                          <TableCell>{serial.BatchNo || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Mobile Card View - always all cards, no pagination */}
              <div className="sm:hidden space-y-3">
                {serialsForMobile.map((serial) => (
                  <div
                    key={serial.Id}
                    className="border rounded-lg p-4 shadow-sm bg-white"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <Checkbox
                        checked={selectedSerials.has(serial.Id)}
                        onCheckedChange={(checked) =>
                          handleCheckboxChange(serial.Id, !!checked)
                        }
                        className="scale-75 mt-1"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-base text-gray-900 break-words mb-1">
                          {serial.SerialNumber}
                        </h4>
                        <p className="text-sm font-medium text-green-600">
                          ₹{serial.Rate.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm pl-8">
                      <div>
                        <span className="font-medium text-gray-700">Mfg Date:</span>
                        <p className="text-gray-900">{formatDate(serial.MfgDate)}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Expiry:</span>
                        <p className="text-gray-900">
                          {serial.ExpiryDays === 999999
                            ? 'Forever'
                            : serial.ExpiryDays}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className="font-medium text-gray-700">Batch No:</span>
                        <p className="text-gray-900 break-words">
                          {serial.BatchNo || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-between items-center p-4 border-t bg-gray-50 shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              {selectedSerials.size} selected
            </span>
            <Button
              onClick={handleAddToList}
              disabled={selectedSerials.size === 0}
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              Add To List
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SerialSelectionPopup;
